import React, { useState, useEffect, useRef, useMemo } from 'react';
import StaticOpponentSelector from './StaticOpponentSelector';
import DataService from '../../XXX/DataService';
import { useChampionDisplayNames } from '../../utils/championNames';
import './MatchupList.css';

const ROLE_ICONS: Record<string, string> = {
  TOP: '/Images/top_icon.png',
  JUNGLE: '/Images/jgl_icon.png',
  MIDDLE: '/Images/mid_icon.png',
  BOTTOM: '/Images/bot_icon.png',
  UTILITY: '/Images/sup_icon.png',
};

const ROLE_LABELS: Record<string, string> = {
  TOP: 'Top',
  JUNGLE: 'Jungle',
  MIDDLE: 'Mid',
  BOTTOM: 'Bot',
  UTILITY: 'Support',
};

interface MatchupData {
  championName: string;
  opponentName: string;
  winRate: number;
  gamesPlayed: number;
  lane?: string;
}

interface MatchupListProps {
  championName: string;
  currentOpponent: string;
  onOpponentSelect: (opponentName: string) => void;
}
// hello
const MatchupList: React.FC<MatchupListProps> = ({ championName, currentOpponent, onOpponentSelect }) => {
  const [matchups, setMatchups] = useState<MatchupData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');
  const containerRef = useRef<HTMLDivElement>(null);
  const { getDisplayName } = useChampionDisplayNames();
  
  // Fetch latest API version
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);

  useEffect(() => {
    const fetchMatchups = async () => {
      if (!championName) return;

      try {
        setLoading(true);
        setError(null);

        // Use the existing database stats endpoint to get matchup data
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/matchups/${championName}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to fetch matchup data');
        }

        // Sort by win rate descending (best matchups first)
        const sortedMatchups = result.data.sort((a: MatchupData, b: MatchupData) => b.winRate - a.winRate);
        setMatchups(sortedMatchups);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error fetching matchups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatchups();
  }, [championName]);

  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        // Container width handling if needed
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate how many items can fit and split strong/weak
  const getDisplayedMatchups = () => {
    if (matchups.length === 0) return { strongest: [], weakest: [], showExpandButton: false };

    // Sort matchups by win rate
    const sortedMatchups = [...matchups].sort((a, b) => b.winRate - a.winRate);
    
    if (isExpanded) {
      return { strongest: sortedMatchups, weakest: [], showExpandButton: true };
    }

    // Always show 6 strongest + 6 weakest + button in the middle (13 total)
    const strongestCount = 7;
    const weakestCount = 7;
    
    if (sortedMatchups.length <= strongestCount + weakestCount) {
      return { strongest: sortedMatchups, weakest: [], showExpandButton: false };
    }

    const strongest = sortedMatchups.slice(0, strongestCount);
    const weakest = sortedMatchups.slice(-weakestCount);
    
    return { strongest, weakest, showExpandButton: true };
  };

  const { strongest, weakest, showExpandButton } = getDisplayedMatchups();

  // Derive the most common lane from matchup data
  const mostCommonLane = useMemo(() => {
    if (matchups.length === 0) return '';
    const laneCounts: Record<string, number> = {};
    for (const m of matchups) {
      if (m.lane) {
        laneCounts[m.lane] = (laneCounts[m.lane] || 0) + m.gamesPlayed;
      }
    }
    const sorted = Object.entries(laneCounts).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? sorted[0][0] : '';
  }, [matchups]);

  if (loading) {
    return (
      <div className="matchup-list-loading">
        <div className="loading-spinner-small"></div>
        <span>Loading matchups...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matchup-list-error">
        <span>Could not load matchup data</span>
      </div>
    );
  }

  if (matchups.length === 0) {
    return (
      <div className="matchup-list-empty">
        <span>No matchup data available for {getDisplayName(championName)}</span>
      </div>
    );
  }

  return (
    <div className="matchup-list-container" ref={containerRef}>
      <div className="matchup-list-header">
        <div className="matchup-list-title-section">
          <h3>
            {mostCommonLane && ROLE_ICONS[mostCommonLane] && (
              <img
                src={ROLE_ICONS[mostCommonLane]}
                alt={ROLE_LABELS[mostCommonLane] || mostCommonLane}
                className="matchup-role-icon"
              />
            )}
            Matchups for {getDisplayName(championName)}
          </h3>
          <span className="matchup-instructions">Select a champion from below or search for one to compare Damage and Combos</span>
        </div>
        <div className="matchup-opponent-selector-section">
          <StaticOpponentSelector
            championId=""
            currentOpponent={currentOpponent}
            onOpponentChange={onOpponentSelect}
            championName={championName}
          />
        </div>
      </div>
      
      <div className={`matchup-list-champions ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Strongest matchups */}
        {strongest.map((matchup) => (
          <div 
            key={`${matchup.championName}-vs-${matchup.opponentName}`}
            className="matchup-champion-card"
            onClick={() => onOpponentSelect(matchup.opponentName)}
          >
            <img 
              src={`https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${matchup.opponentName}.png`}
              alt={matchup.opponentName}
              className="matchup-champion-icon"
            />
            <div className={`matchup-winrate ${matchup.winRate >= 50 ? 'positive' : 'negative'}`}>
              {matchup.winRate.toFixed(1)}%
            </div>
            <div className="matchup-games">
              {matchup.gamesPlayed}
            </div>
          </div>
        ))}
        
        {/* Expand/Collapse button */}
        {showExpandButton && (
          <button 
            className="matchup-expand-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '←' : '...'}
          </button>
        )}
        
        {/* Weakest matchups (only show when collapsed) */}
        {!isExpanded && weakest.map((matchup) => (
          <div 
            key={`${matchup.championName}-vs-${matchup.opponentName}`}
            className="matchup-champion-card"
            onClick={() => onOpponentSelect(matchup.opponentName)}
          >
            <img 
              src={`https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${matchup.opponentName}.png`}
              alt={matchup.opponentName}
              className="matchup-champion-icon"
            />
            <div className={`matchup-winrate ${matchup.winRate >= 50 ? 'positive' : 'negative'}`}>
              {matchup.winRate.toFixed(1)}%
            </div>
            <div className="matchup-games">
              {matchup.gamesPlayed}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MatchupList; 